import React, { useEffect, useState } from 'react'
import { Helmet, HelmetProvider } from 'react-helmet-async'
import utils from '../../utils'
import { useParams } from 'react-router-dom'
import houseServices from '../../services/HousesService'
import Loading from '../../components/feedback/Loading'
import FetchError from '../../components/feedback/FetchError'
import ReactPlayer from 'react-player';
import { Link } from 'react-router-dom';
import {
    LuChevronRight, LuBed, LuBath, LuSquare, LuLayers,
    LuBadgeCheck, LuPlay,
} from "react-icons/lu";
import { BsFiletypeDoc } from 'react-icons/bs';

function HouseMediaItem({ media, setActiveImage }) {
    function openPdf(url) {
        window.open(url);
    }

    return (
        <div className="aspect-[4/3] rounded-xl overflow-hidden cursor-pointer transition-all border-2 border-transparent opacity-70 hover:opacity-100 hover:border-primary">
            {utils.isImage(media?.file) ? (
                <img
                    onClick={() => setActiveImage(utils.baseUrl + media?.file)}
                    className="w-full h-full object-cover"
                    src={utils.baseUrl + media?.file}
                    alt="House media"
                />
            ) : (
                <div
                    onClick={() => openPdf(utils.baseUrl + media?.file)}
                    className="w-full h-full flex items-center justify-center bg-surface-container-high text-primary"
                >
                    <BsFiletypeDoc size={32} />
                </div>
            )}
        </div>
    );
}

function HouseDetails({ house, openMedia, activeImage, setActiveImage }) {
    return (
        <main className="bg-white">
            <section className="bg-surface-container-low border-b border-outline-variant/10 py-16 mb-16">
                <div className="container mx-auto px-3">
                    <nav className="flex mb-4 gap-2 text-[10px] font-black uppercase tracking-widest text-on-surface-variant">
                        <Link className="hover:text-primary transition-colors" to="/">Home</Link>
                        <LuChevronRight size={14} />
                        <Link className="hover:text-primary transition-colors" to="/houses">All Plans</Link>
                        <LuChevronRight size={14} />
                        <span className="text-primary">{house?.name}</span>
                    </nav>
                    <h1 className="text-5xl font-headline font-black text-on-surface tracking-tight mb-2">
                        {house?.name}
                    </h1>
                    <p className="text-on-surface-variant text-lg max-w-2xl font-medium">
                        {house?.category?.name} design — High-end engineering meets minimalist aesthetics.
                    </p>
                </div>
            </section>
            <section className="container mx-auto px-3">
                <div className="flex flex-col lg:flex-row gap-12">
                    <div className="lg:w-2/3 space-y-16">
                        <div className="space-y-4">
                            <div className="aspect-[16/9] w-full rounded-3xl overflow-hidden shadow-2xl bg-surface-variant border border-outline-variant/10">
                                <img
                                    alt={house?.name}
                                    className="w-full h-full object-cover transition-all duration-500"
                                    src={activeImage}
                                />
                            </div>
                            <div className="grid grid-cols-5 gap-4">
                                <div
                                    onClick={() => setActiveImage(utils.baseUrl + house?.image)}
                                    className={`aspect-[4/3] rounded-xl overflow-hidden cursor-pointer transition-all border-2 ${activeImage === (utils.baseUrl + house?.image) ? 'border-primary shadow-lg shadow-primary/20' : 'border-transparent opacity-70 hover:opacity-100'}`}
                                >
                                    <img alt="Main thumbnail" className="w-full h-full object-cover" src={utils.baseUrl + house?.image} />
                                </div>
                                {openMedia && openMedia.map((media, idx) => (
                                    <HouseMediaItem
                                        key={idx}
                                        media={media}
                                        setActiveImage={setActiveImage}
                                    />
                                ))}
                            </div>
                        </div>
                        <div className="space-y-6">
                            <h2 className="text-3xl font-headline font-black text-on-surface tracking-tight">About This Plan</h2>
                            <div className="prose prose-lg max-w-none text-on-surface-variant leading-relaxed">
                                <p className="font-medium text-on-surface">{house?.description}</p>
                            </div>
                        </div>
                        <div className="space-y-8">
                            <h2 className="text-3xl font-headline font-black text-on-surface tracking-tight">Walkthrough & Layout</h2>
                            <div className="bg-white rounded-3xl overflow-hidden shadow-xl border border-outline-variant/10">
                                {house?.video_url ? (
                                    <div className="aspect-video w-full bg-black">
                                        <ReactPlayer
                                            url={house?.video_url}
                                            playing={false}
                                            controls={true}
                                            width="100%"
                                            height="100%"
                                        />
                                    </div>
                                ) : (
                                    <div className="p-20 flex flex-col items-center justify-center text-center space-y-4 bg-surface-container-low/30">
                                        <LuPlay className="text-primary/20" size={48} />
                                        <p className="font-black uppercase tracking-widest text-[10px] text-on-surface-variant/50">Technical Walkthrough Not Available</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="lg:w-1/3">
                        <div className="sticky top-28 space-y-6">
                            <div className="bg-white rounded-3xl shadow-[0_40px_80px_-15px_rgba(26,42,74,0.1)] overflow-hidden border border-outline-variant/20">
                                <div className="bg-primary p-8 text-white">
                                    <div className="text-3xl font-headline font-black">
                                        Building Specifications
                                    </div>
                                </div>
                                <div className="p-8 space-y-8">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="flex items-center gap-3 p-4 bg-surface-container-low rounded-2xl border border-outline-variant/5">
                                            <LuBed className="text-primary" size={20} />
                                            <div>
                                                <div className="text-[9px] uppercase font-black text-outline tracking-wider">Bedrooms</div>
                                                <div className="font-black text-on-surface">{house?.bedrooms}</div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3 p-4 bg-surface-container-low rounded-2xl border border-outline-variant/5">
                                            <LuBath className="text-primary" size={20} />
                                            <div>
                                                <div className="text-[9px] uppercase font-black text-outline tracking-wider">Baths</div>
                                                <div className="font-black text-on-surface">{house?.bathrooms}</div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3 p-4 bg-surface-container-low rounded-2xl border border-outline-variant/5">
                                            <LuSquare className="text-primary" size={20} />
                                            <div>
                                                <div className="text-[9px] uppercase font-black text-outline tracking-wider">Area</div>
                                                <div className="font-black text-on-surface">{house?.square_meters || '-'} sqft</div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3 p-4 bg-surface-container-low rounded-2xl border border-outline-variant/5">
                                            <LuLayers className="text-primary" size={20} />
                                            <div>
                                                <div className="text-[9px] uppercase font-black text-outline tracking-wider">Stories</div>
                                                <div className="font-black text-on-surface">{house?.stories}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="p-6 bg-surface-container-low/50 rounded-3xl flex items-center gap-5 border border-outline-variant/10">
                                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                                    <LuBadgeCheck size={24} />
                                </div>
                                <div>
                                    <h4 className="font-black text-xs uppercase tracking-widest text-on-surface">Verified Plan</h4>
                                    <p className="text-[11px] font-medium text-on-surface-variant leading-tight mt-1">Structurally sound and ready for site-specific engineering.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
}

function HouseViewPage(props) {
    const [house, setHouse] = useState()
    const url = window.location.href

    const houseParams = useParams()
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState()
    const [openMedia, setOpenMedia] = useState([])
    const [activeImage, setActiveImage] = useState()

    useEffect(() => {
        houseServices.getHouse(houseParams?.slug).then(
            res => {
                setHouse(res.data)
                setActiveImage(utils.baseUrl + res.data?.image)
                setLoading(false)
                houseServices.getHouseMedia(houseParams?.slug).then(
                    res => {
                        setOpenMedia(res.data)
                    },
                    err => {
                        console.log(err);
                    }
                )
                // houseServices.getComments(houseParams?.slug).then(
                //     res => {
                //         setComments(res.data)
                //     },
                //     err => {
                //         toast.error(utils.toasterError)
                //     }
                // )
            },
            err => {
                setError(err)
                setLoading(false)
            }
        )
    }, [houseParams?.slug])
    useEffect(() => { }, [error])
    useEffect(() => { }, [loading])
    // useEffect(() => {}, [comments])
    useEffect(() => {}, [house])
    useEffect(() => { }, [activeImage])
    // useEffect(() => {}, [cartLoading])
    useEffect(() => { }, [openMedia])
    // useEffect(() => {}, [
    //     closedMedia, inPurchases, houseParams?.slug, openMedia
    // ])
    // useEffect(() => {
    //     if (houseMedia) {
    //         setOpenMedia(houseMedia.filter((media) => media?.show_premium === false))
    //         setClosedMedia(houseMedia.filter((media) => media?.show_premium === true))
    //     }
    // }, [houseMedia])

    // function submitHandler(details, commentRef){
    //     houseServices.createComment(details).then(
    //         res => {
    //             toast.success("Comment posted succesfully")
    //             commentRef.current.value = null
    //             setComments([res.data, ...comments])
    //         },
    //         err => {
    //             console.log(err);
    //         }
    //     )
    // }
    // function addToCart(){
    //     if (!isAuth) {
    //         return
    //     }
    //     if (inCart) {
    //         toast.error("Item is already in your cart")
    //         return
    //     }
    //     const formData = new FormData()
    //     formData.append("house", house?.id)
    //     const cartId = cartCtx.getCartId()
    //     if (cartId) {
    //         formData.append("cart", cartCtx.getCartId())
    //     } else{
    //         console.log("no cart id");
    //         return
    //     }
    //     formData.append("total", parseFloat(house?.price))
    //     setCartLoading(true)
    //     cartServices.addCartItem(formData).then(
    //         res => {
    //             const data = res.data
    //             cartCtx.setItemSlugs(data?.slugs)
    //             cartCtx.setNumItems(data?.num_items)
    //             toast.success("Item added to cart successfully")
    //             setCartLoading(false)
    //         }, 
    //         err => {
    //             console.log(err);
    //             setCartLoading(false)
    //         }
    //     )
    // }
    // function downloadMedia(){
    //     const purchasedItems = cartCtx.purchasedItems
    //     if (subscriptionType !== "Active") {
    //         const item = purchasedItems.filter((item) => item?.slug === house?.slug)
    //         if (!item.length) {
    //             console.log("no item");
    //             toast.error(utils.toasterError)
    //             setDownloadLoading(false)
    //             return
    //         }
    //         downloadItem(item[0]?.id, 0)
    //     }        
    //     downloadItem(house?.id, 1)
    // }
    // function downloadItem(id, action){
    //     setDownloadLoading(true)
    //     paymentServices.downloadItemMedia(id, action).then(
    //         res => {
    //             utils.downloadStream(res.data, house?.slug)
    //             setDownloadLoading(false)
    //         },
    //         err => {
    //             setDownloadLoading(false)
    //             toast.error(utils.toasterError)
    //         }
    //     )
    // }
    return (
        <HelmetProvider>
            <div>
                <div className=''>
                    {loading ? (
                        <Loading />
                    ) : (
                        <div>
                            {error ? (
                                <div className="container mx-auto px-3 mb-16">
                                    <FetchError error={error} />
                                </div>
                            ) : (
                                <div className=" mb-16">
                                    <Helmet>
                                        <title>{house?.name} - {utils.siteName}</title>
                                        <meta property="description" content={house?.description} />

                                        <meta property="og:title" content={house?.name} />
                                        <meta property="og:image" content={utils.baseUrl + house?.image} />
                                        <meta property="og:url" content={url} />
                                        <meta property="og:description" content={house?.description} />

                                        <meta name="twitter:card" content={utils.baseUrl + house?.image} />
                                        <meta name="twitter:title" content={house?.name} />
                                        <meta name="twitter:description" content={house?.description || "Join us for an exciting event"} />
                                        <meta name="twitter:image" content={utils.baseUrl + house?.image} />
                                        <meta name="twitter:url" content={url} />

                                        <link rel="canonical" href={url} />

                                        <script type="application/ld+json">
                                            {`
                                                {
                                                    "@context": "https://schema.org",
                                                    "@type": "Architectural Plan",
                                                    "name": "${house?.name}",
                                                    "image": ["${utils.baseUrl + house?.image}"],
                                                    "description": "${house?.description}"
                                                }
                                            `}
                                        </script>
                                    </Helmet>
                                    <HouseDetails
                                        house={house}
                                        activeImage={activeImage}
                                        setActiveImage={setActiveImage}
                                        openMedia={openMedia}
                                    />
                                    {/* <div className="flex justify-center mb-16">
                                        <div className="w-11/12 md:w-10/12">
                                            <h3 className="text-2xl mb-3">Comments: <span>{comments.length}</span></h3>
                                            <CommentForm
                                                submitHandler={submitHandler}
                                                house={house}
                                            />
                                            <CommentList
                                                comments={comments}
                                            />
                                        </div>
                                    </div> */}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </HelmetProvider>
    )
}

export default HouseViewPage